// One license: its keys, what it entitles, the installations running under it, where each may be
// signed in from, and who may sign in to each of them.
import { useEffect, useState, type FormEvent } from 'react'
import { errorMessage, formatDate, portal, type InstallationDetail, type LicenseDetail } from '../api'
import { Badge, Card, Copyable, EmptyState, ErrorText, Facts, Icon, Link, Notice, PageHeader, Snippet, Spinner, Stat, Stats } from '../components/Ui'

export function LicensePage({ id }: { id: string }) {
  const [detail, setDetail] = useState<LicenseDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState('')

  async function load() {
    try {
      const d = await portal.license(id)
      setDetail(d)
      setName(d.info.name)
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  useEffect(() => {
    setDetail(null)
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when the id changes
  }, [id])

  async function run(action: () => Promise<unknown>) {
    setActionError(null)
    try {
      await action()
      await load()
    } catch (err) {
      setActionError(errorMessage(err))
    }
  }

  async function rename(e: FormEvent) {
    e.preventDefault()
    await run(() => portal.rename(id, name))
    setRenaming(false)
  }

  if (error) {
    return (
      <>
        <ErrorText error={error} />
        <Link to="/" className="crumb">
          <Icon name="arrow-left" size={14} /> Back to your licenses
        </Link>
      </>
    )
  }
  if (!detail) return <Spinner label="Loading the license…" />
  const { info, apiKeys, installations } = detail
  const status = info.disabled ? <Badge tone="bad">Disabled</Badge> : info.expired ? <Badge tone="bad">Expired</Badge> : <Badge tone="good">Active</Badge>
  const enabledKeys = apiKeys.filter((k) => !k.disabled)
  const signInReady = installations.filter((i) => i.approved && i.hosts.length > 0).length
  const count = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

  return (
    <>
      <PageHeader
        crumb={
          <Link to="/" className="crumb">
            <Icon name="arrow-left" size={14} /> Your licenses
          </Link>
        }
        title={
          renaming ? (
            <form className="row" onSubmit={rename}>
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} aria-label="License name" />
              <button className="button button-primary button-small">Save</button>
              <button type="button" className="button button-quiet button-small" onClick={() => setRenaming(false)}>
                Cancel
              </button>
            </form>
          ) : (
            <h1>{info.name}</h1>
          )
        }
        aside={
          !renaming && (
            <>
              {status}
              <button type="button" className="button button-quiet button-small" onClick={() => setRenaming(true)}>
                Rename
              </button>
            </>
          )
        }
        lead="Everything this license holds: the keys an installation identifies itself with, what it entitles that installation to, and the installations that have reported in under it."
      />
      <ErrorText error={actionError} />

      <Stats>
        <Stat
          label="Status"
          value={status}
          hint={info.disabled ? 'Installations under it are refused' : info.expiresUtc ? `Expires ${formatDate(info.expiresUtc)}` : 'Does not expire'}
        />
        <Stat label="API keys" value={apiKeys.length} hint={`${enabledKeys.length} enabled`} />
        <Stat label="Installations" value={installations.length} hint={`${signInReady} ready for sign-in`} />
        <Stat
          label="Entitlements"
          value={info.features.length + info.limits.length + info.accounts.length}
          hint={`${count(info.features.length, 'feature')}, ${count(info.limits.length, 'limit')}, ${count(info.accounts.length, 'credit account')}`}
        />
      </Stats>

      <Card
        title="Keys"
        lead="An installation identifies itself with the license key and one API key."
        actions={
          <button type="button" className="button button-small" onClick={() => run(() => portal.createApiKey(id))}>
            <Icon name="plus" size={14} />
            New API key
          </button>
        }
      >
        <div className="grid-2">
          <div className="stack">
            <p className="muted">
              Both go into the installation's <code>relatude.db.json</code>, or on the Security page of its admin UI. The license key names the license and
              never changes. API keys are the secret, and there can be several: disable one to shut out whatever holds it at once, or make a new one first
              and swap them to rotate without downtime.
            </p>
            <Copyable label="License key" value={info.id} />
          </div>
          <Snippet
            title="relatude.db.json"
            lines={[
              { key: 'LicenseKey', value: info.id },
              { key: 'ApiKey', value: enabledKeys[0]?.id ?? 'an enabled API key from the list' },
            ]}
          />
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>API key</th>
                <th>Status</th>
                <th>Expires</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((k) => (
                <tr key={k.id}>
                  <td>
                    <Copyable value={k.id} />
                  </td>
                  <td>{k.disabled ? <Badge tone="bad">Disabled</Badge> : <Badge tone="good">Enabled</Badge>}</td>
                  <td>{k.canExpire ? formatDate(k.expirationUtc) : 'Never'}</td>
                  <td className="actions">
                    <button type="button" className="button button-small" onClick={() => run(() => portal.updateApiKey(id, k.id, !k.disabled))}>
                      {k.disabled ? 'Enable' : 'Disable'}
                    </button>
                    <button
                      type="button"
                      className="button button-small button-danger"
                      onClick={() => {
                        if (window.confirm('Delete this API key? Anything still using it stops working at once.')) void run(() => portal.deleteApiKey(id, k.id))
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card
        title="Entitlements"
        lead="What an installation running under this license may do. They come from the product bundle the license was created from, and are changed on the server rather than here."
      >
        {info.features.length === 0 && info.limits.length === 0 && info.accounts.length === 0 ? (
          <p className="muted">This license carries no features, limits or credits.</p>
        ) : (
          <div className="entitlement-grid">
            {info.features.length > 0 && (
              <div>
                <h3>Features</h3>
                <div className="chips">
                  {info.features.map((f) => (
                    <span key={f} className="chip">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {info.limits.length > 0 && (
              <div>
                <h3>Limits</h3>
                <table className="table table-compact">
                  <tbody>
                    {info.limits.map((l) => (
                      <tr key={l.name}>
                        <td>{l.name}</td>
                        <td className="num">{l.unlimited ? 'Unlimited' : l.maxValue.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {info.accounts.length > 0 && (
              <div>
                <h3>Monthly credits</h3>
                <table className="table table-compact">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th className="num">Used</th>
                      <th className="num">Of</th>
                    </tr>
                  </thead>
                  <tbody>
                    {info.accounts.map((a) => (
                      <tr key={a.name}>
                        <td>{a.name}</td>
                        <td className="num">{a.usedThisMonth.toLocaleString()}</td>
                        <td className="num">{a.monthlyLimit.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {(info.stopEdit || info.stopVisit || info.messageToAllEditors || info.messageToAllVisitors) && (
          <Notice kind="warn">
            {info.stopVisit && <div>Visiting is stopped on this license.</div>}
            {info.stopEdit && <div>Editing is stopped on this license.</div>}
            {info.messageToAllEditors && <div>Message to editors: {info.messageToAllEditors}</div>}
            {info.messageToAllVisitors && <div>Message to visitors: {info.messageToAllVisitors}</div>}
          </Notice>
        )}
      </Card>

      <Card title="Installations" lead="Every Relatude.DB installation that has reported in with this license's keys.">
        <p className="muted">
          Signing in with Relatude.License works at an installation only after you have approved it, and the address its admin UI is opened on. As the
          owner you can then sign in to every approved installation; other registered users only where you grant it, by the email address or mobile
          number they registered with.
        </p>
        {installations.length === 0 ? (
          <EmptyState icon="server" title="No installations yet">
            An installation appears here the first time it reports in, or asks to sign in, with this license's keys.
          </EmptyState>
        ) : (
          installations.map((inst) => <Installation key={inst.id} licenseId={id} installation={inst} onChanged={load} onError={setActionError} />)
        )}
      </Card>
    </>
  )
}

function Installation({ licenseId, installation, onChanged, onError }: { licenseId: string; installation: InstallationDetail; onChanged: () => Promise<void>; onError: (m: string | null) => void }) {
  const [identifier, setIdentifier] = useState('')
  const [host, setHost] = useState('')
  const [busy, setBusy] = useState(false)

  async function act(action: () => Promise<unknown>, after?: () => void) {
    setBusy(true)
    onError(null)
    try {
      await action()
      after?.()
      await onChanged()
    } catch (err) {
      onError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  function grant(e: FormEvent) {
    e.preventDefault()
    void act(() => portal.grant(licenseId, installation.id, identifier), () => setIdentifier(''))
  }

  function addHost(e: FormEvent) {
    e.preventDefault()
    void act(() => portal.approveHost(licenseId, installation.id, host), () => setHost(''))
  }

  const usable = installation.approved && installation.hosts.length > 0

  return (
    <article className="installation">
      <div className="installation-head">
        <div>
          <div className="installation-title">
            <Icon name="server" />
            <span>{installation.name}</span>
            {usable ? (
              <Badge tone="good">Sign-in approved</Badge>
            ) : installation.approved ? (
              <Badge tone="warn">No approved address</Badge>
            ) : (
              <Badge tone="warn">Not approved for sign-in</Badge>
            )}
          </div>
          <div className="muted small">
            <code>{installation.installationKey}</code>
          </div>
        </div>
        {installation.hosts.length > 0 && (
          <button
            type="button"
            className={'button button-small' + (installation.approved ? ' button-danger' : ' button-primary')}
            disabled={busy}
            onClick={() => act(() => portal.setInstallationApproved(licenseId, installation.id, !installation.approved))}
          >
            {installation.approved ? 'Revoke sign-in' : 'Approve installation'}
          </button>
        )}
      </div>

      <div className="installation-body">
        <Facts
          items={[
            { label: 'Last contact', value: formatDate(installation.lastContact) },
            { label: 'From', value: installation.lastContactIp },
            { label: 'Server', value: installation.serverName },
            { label: 'Machine', value: installation.machineName },
            { label: 'Relatude.DB', value: installation.buildVersion },
            { label: 'Reported addresses', value: installation.externalAddresses.join(', ') },
            { label: 'Users', value: `${installation.users} (${installation.editors} editors, ${installation.admins} admins)` },
            { label: 'Nodes', value: installation.nodes.toLocaleString() },
          ]}
        />

        <div className="installation-sections">
          <section className="installation-access">
            <h4>
              <Icon name="globe" size={16} /> Where it may be signed in from
            </h4>
            <p className="muted small">
              The addresses the admin UI is opened on. An address shows up as waiting the first time someone tries to sign in from it. Approve it only if it
              really is this installation's: anyone holding the license keys could ask from an address of their own, and the address is the one thing they
              cannot fake.
            </p>
            {installation.pendingHosts.length > 0 && (
              <ul className="user-list">
                {installation.pendingHosts.map((h) => (
                  <li key={h}>
                    <span>
                      <code>{h}</code> <Badge tone="warn">waiting for approval</Badge>
                    </span>
                    <span className="row-tight">
                      <button type="button" className="button button-small button-primary" disabled={busy} onClick={() => act(() => portal.approveHost(licenseId, installation.id, h))}>
                        Approve
                      </button>
                      <button type="button" className="button button-small button-quiet" disabled={busy} onClick={() => act(() => portal.dismissPendingHost(licenseId, installation.id, h))}>
                        Dismiss
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {installation.hosts.length > 0 ? (
              <ul className="user-list">
                {installation.hosts.map((h) => (
                  <li key={h}>
                    <span>
                      <code>{h}</code> <Badge tone="good">approved</Badge>
                    </span>
                    <button type="button" className="button button-small button-quiet" disabled={busy} onClick={() => act(() => portal.removeHost(licenseId, installation.id, h))}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              installation.pendingHosts.length === 0 && (
                <p className="muted small">No address yet. Add the one the admin UI is opened on, or wait for the first sign-in attempt to report it.</p>
              )
            )}
            <form className="row" onSubmit={addHost}>
              <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="site.example or site.example:8443" aria-label="Address" />
              <button className="button button-small" disabled={busy || host.trim().length < 3}>
                {busy ? 'Working…' : 'Add address'}
              </button>
            </form>
          </section>

          <section className="installation-access">
            <h4>
              <Icon name="users" size={16} /> Who may sign in
            </h4>
            <p className="muted small">
              You, as the owner of the license, always can. Anyone else needs a grant here, by the email address or mobile number they registered with at
              Relatude.License.
            </p>
            {installation.grantedUsers.length === 0 ? (
              <p className="muted small">Only you so far.</p>
            ) : (
              <ul className="user-list">
                {installation.grantedUsers.map((u) => (
                  <li key={u.id}>
                    <span>
                      {u.name || u.email || u.mobile}
                      {u.name && (u.email || u.mobile) && <span className="muted small"> · {u.email || u.mobile}</span>}
                    </span>
                    <button type="button" className="button button-small button-quiet" disabled={busy} onClick={() => act(() => portal.revoke(licenseId, installation.id, u.id))}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <form className="row" onSubmit={grant}>
              <input
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="email or mobile number of a registered user"
                aria-label="Email or mobile number"
              />
              <button className="button button-small" disabled={busy || identifier.trim().length < 3}>
                {busy ? 'Working…' : 'Grant access'}
              </button>
            </form>
          </section>
        </div>
      </div>
    </article>
  )
}
